import React from 'react';
import { Box, BoxProps } from '@chakra-ui/react';

interface IFaIconSpecific {
  clickable?: boolean;
  altBaseClassName?: string;
  iconName: string;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export interface IFaIcon
  extends Omit<BoxProps, 'onClick' | 'marginTop' | 'marginRight' | 'paddingLeft'>, IFaIconSpecific {
  // Legacy props maintained for backward compatibility
  marginTop?: string;
  marginRight?: string;
  paddingLeft?: string;
}

/**
 * FaIcon component - FontAwesome icon wrapper built with Chakra UI v3
 *
 * Replaces styled-components with Chakra UI Box component for FontAwesome icons.
 * Provides flexible styling and proper cursor behavior for clickable icons.
 *
 * Features:
 * - FontAwesome icon support with configurable base class
 * - Clickable state with proper cursor styling
 * - Chakra UI styling system integration
 * - Legacy prop support for margin and padding
 * - TypeScript support with comprehensive interfaces
 *
 * @example
 * ```tsx
 * <FaIcon iconName="fa-home" clickable onClick={handleClick} />
 * <FaIcon iconName="fa-user" altBaseClassName="far" />
 * <FaIcon iconName="fa-star" color="blue.500" fontSize="lg" />
 * ```
 */
const FaIcon: React.FC<IFaIcon> = ({
  iconName,
  clickable,
  altBaseClassName,
  marginTop,
  marginRight,
  paddingLeft,
  onClick,
  ...boxProps
}) => {
  const base = altBaseClassName ?? 'fas';

  return (
    <Box
      as='i'
      className={`${base} ${iconName}`}
      cursor={clickable ? 'pointer' : 'auto'}
      marginTop={marginTop}
      marginRight={marginRight}
      paddingLeft={paddingLeft}
      onClick={onClick}
      {...boxProps}
    />
  );
};

export default FaIcon;
